import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { CLIENT_GROUPED_QUERY_KEY } from '@/app/(main)/client-detail/hooks/useGetClientGroupedQuery';
import { CLIENT_CREATE_QUERY_KEY, RELATED_TRAVELERS_QUERY_KEY } from '@/app/(main)/clients/create/const/const';
import { useCreateClientPassportData } from '@/app/(main)/clients/create/hooks/useCreateClientPassportData';
import { CLIENT_PASSPORT_DATA_QUERY_KEY } from '@/app/(main)/clients/create/hooks/useGetClientPassportData';
import { useUpdateClientPassportData } from '@/app/(main)/clients/create/hooks/useUpdateClientPassportData';
import { useEditClientMutation } from '@/app/(main)/clients/hooks/useEditClientMutation';
import type { CreateClientFormData } from '@/types/clientsApiTypes';
import type { EditClientData } from '@/types/clientsApiTypes';
import { formatMMDDYYYYToYYYYMMDD } from '@/utils/formatDate';
import { nullableString } from '@/utils/validations';
import { EditTravelDataForm } from '../../EditSectionModals/components/EditTravelDataForm';
import { ModalKeys } from '../../EditSectionModals/types';
import { defineSection } from '../../EditSectionModals/utils/defineSection';
import { CLIENT_DETAILS_QUERY_KEY } from '@/app/(main)/client-detail/consts/client-detail';

const schema = yup.object({
  primary_passenger: yup.object().shape({
    tsa: yup.object({
      global_entry: nullableString().defined(),
      global_expiry_date: nullableString().defined(),
      known_traveler_number: nullableString().defined(),
      known_traveler_number_expire_date: nullableString().defined(),
    }),
    passport: yup.object({
      passport_number: nullableString().defined(),
      expiry_date: nullableString().defined(),
      passport_id: nullableString().defined(),
    }),
  }),
});

export type TravelDataFormValues = yup.InferType<typeof schema>;

export type TravelDataPayload = {
  clientId: number;
  tsa: EditClientData['tsa'];
  passport: Pick<EditClientData['passport'], 'passport_number' | 'expiry_date' | 'passport_id'>;
};

function useTravelDataOnSave() {
  const editMutation = useEditClientMutation();
  const queryClient = useQueryClient();
  const { mutateAsync: createPassport } = useCreateClientPassportData();
  const { mutateAsync: updatePassport } = useUpdateClientPassportData();

  return async (payload: TravelDataPayload) => {
    const passportId = payload.passport.passport_id;
    const clientId = String(payload.clientId);

    const savePassport = async (passportNumber: string | null, expiryDate: string | null) => {
      const expiryYYYYMMDD = expiryDate ? formatMMDDYYYYToYYYYMMDD(expiryDate) : null;
      try {
        if (passportId) {
          try {
            await updatePassport({
              passportId: String(passportId),
              data: { passport_number: passportNumber ?? null, expiry_date: expiryYYYYMMDD ?? null },
            });
          } catch (e: unknown) {
            const status = typeof e === 'object' && e !== null && 'status' in e ? (e as { status: number }).status : 0;
            if (status === 404 && (passportNumber?.trim() || expiryYYYYMMDD)) {
              await createPassport(
                {
                  clientId,
                  data: {
                    passport_number: passportNumber?.trim() ?? '',
                    expiry_date: expiryYYYYMMDD ?? '',
                  },
                },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: [CLIENT_PASSPORT_DATA_QUERY_KEY] });
                    queryClient.invalidateQueries({ queryKey: [CLIENT_CREATE_QUERY_KEY] });
                    queryClient.invalidateQueries({ queryKey: [CLIENT_DETAILS_QUERY_KEY] });
                  },
                }
              );
              return;
            }
            throw e;
          }
          queryClient.invalidateQueries({ queryKey: [CLIENT_PASSPORT_DATA_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [CLIENT_DETAILS_QUERY_KEY] });
          return;
        }

        if (!passportNumber?.trim() && !expiryYYYYMMDD) return;
        await createPassport(
          {
            clientId,
            data: { passport_number: passportNumber?.trim() ?? '', expiry_date: expiryYYYYMMDD ?? '' },
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: [CLIENT_PASSPORT_DATA_QUERY_KEY] });
              queryClient.invalidateQueries({ queryKey: [CLIENT_DETAILS_QUERY_KEY] });
            },
          }
        );
      } catch {
        toast.error('Failed to save passport data');
      }
    };

    await savePassport(payload.passport.passport_number ?? null, payload.passport.expiry_date ?? null);
    await editMutation.mutateAsync(
      {
        data: { tsa: payload.tsa },
        clientId: payload.clientId,
      },
      {
        onSuccess: () => {
          toast.success('Client updated successfully');
          queryClient.invalidateQueries({ queryKey: [CLIENT_CREATE_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [RELATED_TRAVELERS_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [CLIENT_DETAILS_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [CLIENT_GROUPED_QUERY_KEY] });
        },
      }
    );
  };
}

function mapValuesToPayload(
  values: TravelDataFormValues,
  client: CreateClientFormData | null | undefined
): TravelDataPayload {
  const clientId = client?.primary_passenger?.client?.client_id ?? 0;
  const tsa = values.primary_passenger.tsa;
  const passport = values.primary_passenger.passport;
  return {
    clientId,
    tsa: {
      global_entry: tsa.global_entry ?? null,
      global_expiry_date: formatMMDDYYYYToYYYYMMDD(tsa.global_expiry_date) ?? null,
      known_traveler_number: tsa.known_traveler_number ?? null,
      known_traveler_number_expire_date: formatMMDDYYYYToYYYYMMDD(tsa.known_traveler_number_expire_date) ?? null,
    },
    passport: {
      passport_number: passport.passport_number ?? null,
      expiry_date: formatMMDDYYYYToYYYYMMDD(passport.expiry_date) ?? null,
      passport_id: passport.passport_id ? Number(passport.passport_id) : undefined,
    },
  };
}

const defaultTsa = {
  global_entry: null,
  global_expiry_date: null,
  known_traveler_number: null,
  known_traveler_number_expire_date: null,
};

export const importantTravelDataSection = defineSection<
  (typeof ModalKeys)['important_travel_data'],
  CreateClientFormData,
  typeof schema,
  TravelDataPayload
>({
  key: ModalKeys.important_travel_data,
  title: 'Important travel data',
  subtitle: 'Client travel identification numbers and related expiration dates.',
  schema,
  mapClientToValues: (c) => ({
    primary_passenger: {
      clientId: c.primary_passenger?.client?.client_id ?? 0,
      tsa: c.primary_passenger?.tsa ?? defaultTsa,
      passport: {
        passport_number: c.primary_passenger?.passport?.passport_number ?? null,
        expiry_date: c.primary_passenger?.passport?.expiry_date ?? null,
        passport_id: c.primary_passenger?.passport?.passport_id
          ? String(c.primary_passenger?.passport?.passport_id)
          : null,
      },
    },
  }),
  mapValuesToPayload,
  useOnSave: useTravelDataOnSave,
  Fields: EditTravelDataForm,
});
